import { type ReactNode, useCallback } from 'react';
import type { Lens } from '@hookform/lenses';
import { useFieldArray } from '@hookform/lenses/rhf';
import { z } from 'zod/v4';
import { Grid } from '@interxfi/design-system/components/alignment/grid';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { Button } from '@interxfi/design-system/components/button/button';
import { PlainIconButton } from '@interxfi/design-system/components/button/plain-icon/plain-icon-button';
import { usPhoneMask } from '@interxfi/design-system/components/input/text-input/masks';
import DeleteIcon from '@interxfi/design-system/icons/react/icon-delete';
import PlusIcon from '@interxfi/design-system/icons/react/icon-plus';
import type { ResponsiveValue } from '@interxfi/design-system/util/responsive';
import type { IxcRegistrationV1Phone } from '@interxfi/types/generated/api';
import { AppSwitchInputWithLens } from '../../../components/form/app-switch-input';
import { AppTextInputWithLens } from '../../../components/form/app-text-input';
import { addDefinedValues } from '../../../util/has-value';

export const phoneFormSchema = z.object({
  label: z.string(),
  number: z.string().nonempty('Number is required'),
  sms: z.boolean(),
  voice: z.boolean(),
});

export type PhoneFormValues = z.infer<typeof phoneFormSchema>;

export const defaultPhoneValues: PhoneFormValues = { label: '', number: '', sms: false, voice: false };

export function formValuesToPhone(phone: PhoneFormValues | undefined): IxcRegistrationV1Phone | undefined {
  if (!phone || !phone.number) {
    return undefined;
  }

  return addDefinedValues<IxcRegistrationV1Phone>(
    {
      number: phone.number,
    },
    {
      label: phone.label,
      sms: phone.sms ? true : undefined,
      voice: phone.voice ? true : undefined,
    },
  );
}

export function getInitialFormValuesForPhone(phone: IxcRegistrationV1Phone): PhoneFormValues | undefined {
  if (!phone) {
    return undefined;
  }

  return {
    ...defaultPhoneValues,
    label: phone.label || '',
    number: phone.number,
    sms: phone.sms ?? false,
    voice: phone.voice ?? false,
  };
}

export function formValuesToPhones(phones: PhoneFormValues[] | undefined): IxcRegistrationV1Phone[] {
  return (phones || []).reduce<IxcRegistrationV1Phone[]>((acc, curr) => {
    const phone = formValuesToPhone(curr);
    if (phone) {
      acc.push(phone);
    }

    return acc;
  }, []);
}

export function getInitialFormValuesForPhones(phones: IxcRegistrationV1Phone[] | undefined): PhoneFormValues[] | undefined {
  if (!phones) {
    return undefined;
  }

  return phones?.reduce<PhoneFormValues[]>((acc, curr) => {
    const phone = getInitialFormValuesForPhone(curr);
    if (phone) {
      acc.push(phone);
    }

    return acc;
  }, []);
}

const phoneNumberColumns: ResponsiveValue<number> = {
  sm: 1,
  md: 2,
};

interface PhoneFormProps {
  labelPlaceholder?: string;
  lens: Lens<PhoneFormValues>;
  numberOnly?: boolean;
  removeButton?: ReactNode; // Optional remove button, if this form is part of an array of phone numbers
}

function PhoneForm({ labelPlaceholder, lens, numberOnly, removeButton }: PhoneFormProps) {
  return (
    <VerticalStack gap={4}>
      <HorizontalStack blockAlign="center" gap={4} wrap="nowrap">
        {removeButton}

        <Grid autoColumns={phoneNumberColumns} gap={4}>
          <AppTextInputWithLens label="Phone Number" lens={lens.focus('number')} maskOptions={usPhoneMask} required />
          {!numberOnly ? <AppTextInputWithLens label="Label" lens={lens.focus('label')} placeholder={labelPlaceholder} /> : null}
        </Grid>
      </HorizontalStack>

      {!numberOnly ? (
        <HorizontalStack gap={4} wrap="nowrap">
          <AppSwitchInputWithLens label="SMS" lens={lens.focus('sms')} />
          <AppSwitchInputWithLens label="Voice" lens={lens.focus('voice')} />
        </HorizontalStack>
      ) : null}
    </VerticalStack>
  );
}

interface PhonesFormProps {
  labelPlaceholder?: string;
  lens: Lens<PhoneFormValues[]>;
  minLength?: number;
  numberOnly?: boolean;
}

export function PhonesForm({ labelPlaceholder, lens, minLength = 0, numberOnly }: PhonesFormProps) {
  const { append, fields, remove } = useFieldArray(lens.interop());

  const handleAppendPhone = useCallback(() => {
    append({ ...defaultPhoneValues });
  }, [append]);

  return (
    <VerticalStack gap={4}>
      {lens.map(fields, (arrayField, l, i) => (
        <PhoneForm
          key={arrayField.id}
          labelPlaceholder={labelPlaceholder}
          lens={l}
          numberOnly={numberOnly}
          removeButton={
            minLength && i < minLength ? null : (
              <PlainIconButton
                aria-label={`Remove phone number #${i + 1}`}
                onClick={() => {
                  remove(i);
                }}
                tone="danger-dk"
              >
                <DeleteIcon aria-hidden height={16} />
              </PlainIconButton>
            )
          }
        />
      ))}

      <Button affixIcon={<PlusIcon aria-hidden />} onClick={handleAppendPhone} size="medium" variant="light">
        Add a Phone
      </Button>
    </VerticalStack>
  );
}
